// NOTE: ToastContainer is already rendered once in the App.tsx. DO NOT render again.
import { IconAlertCircleFilled, IconAlertTriangleFilled, IconCircleCheckFilled, IconInfoCircleFilled, IconX } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import {
    Slide, toast, ToastTransition, ToastPosition, ToastOptions,
} from 'react-toastify';
import './useToast.scss';
import React from 'react';

 
export enum ToastType {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

export const baseToastDefaults: Partial<ToastOptions> = {
    position: 'bottom-right' as ToastPosition,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    autoClose: 3000,
    transition: Slide as ToastTransition,
};

const useToast = () => {
    const iconMap: Record<ToastType, JSX.Element> = {
        [ToastType.Success]: <NSIcon className="ToastContainer__icon--success my-auto" size="lg" icon={IconCircleCheckFilled} />,
        [ToastType.Warning]: <NSIcon className="ToastContainer__icon--warning my-auto" size="lg" icon={IconAlertTriangleFilled} />,
        [ToastType.Error]: <NSIcon className="ToastContainer__icon--danger my-auto" size="lg" icon={IconAlertCircleFilled} />,
        [ToastType.Info]: <NSIcon className="ToastContainer__icon--info my-auto" size="lg" icon={IconInfoCircleFilled} />,
    };

    const showToast = (
        message: string | JSX.Element,
        type: ToastType,
        opts: Partial<ToastOptions> = {},
        toastId?: React.ReactText, // if toastId is provided, it will update the existing toast
    ): React.ReactText => {
        const toastDefaults = toastId ? {} : baseToastDefaults;
        const mergedOpts = {
            icon: iconMap[type],
            className: `ToastContainer ToastContainer--${type}`,
            closeButton: (props: any) => (
                <NSIcon
                    className="ToastContainer__close-button my-auto"
                    icon={IconX}
                    size="lg"
                    onClick={() => props.closeToast({})}
                />
            ),
            ...toastDefaults,
            ...opts,
        };

        const isToastActive = toastId && toast.isActive(toastId);
        if (isToastActive) {
            toast.update(toastId, {
                render: <div className="my-auto">{message}</div>,
                ...mergedOpts,
            });
            return toastId;
        }
        return toast(<div className="my-auto">{message}</div>, {
            ...mergedOpts,
            type,
        });
    };

    return {
        showSuccess: (message: string, opts?: Partial<ToastOptions>) => showToast(message, ToastType.Success, opts),
        showWarning: (message: string, opts?: Partial<ToastOptions>) => showToast(message, ToastType.Warning, opts),
        showError: (message: string, opts?: Partial<ToastOptions>) => showToast(message, ToastType.Error, opts),
        showToast,
    };
};

export default useToast;
